import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

function FinancialMetricTable({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  selectedCompany,
  selectedDates,
  orgLength,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimDateKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    DebtorDays: '',
    CurrentRatio: '',
    Roi: '',
    TotalIncome: '',
    TotalExpense: '',
    Profit: '',
    PercentProfit: '',
    CashBalance: '',
    SafetyBankBalance: '',
    AverageMonthlyRevenue: '',
    ProfitMargin: '',
    DebtToEquityRatio: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'DimDateKey':
      case 'DebtorDays':
      case 'CurrentRatio':
      case 'Roi':
      case 'TotalIncome':
      case 'TotalExpense':
      case 'Profit':
      case 'PercentProfit':
      case 'CashBalance':
      case 'SafetyBankBalance':
      case 'AverageMonthlyRevenue':
      case 'ProfitMargin':
      case 'DebtToEquityRatio':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;
    let startDate, endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate = new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    let filterRows = [];
    if (activeFilter) {
      filterRows = rows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate,
      );
    } else filterRows = rows;

    filterRows.sort((a, b) => b.DimDateKey - a.DimDateKey);
    setTableRows(filterRows);
  }, [rows, activeFilter, companies, selectedCompany]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimDateKey',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('DebtorDays') && item.hasOwnProperty('CurrentRatio') && item.hasOwnProperty('Roi')
        && item.hasOwnProperty('TotalIncome') && item.hasOwnProperty('TotalExpense') && item.hasOwnProperty('Profit')
        && item.hasOwnProperty('PercentProfit') && item.hasOwnProperty('CashBalance') && item.hasOwnProperty('SafetyBankBalance')
        && item.hasOwnProperty('AverageMonthlyRevenue') && item.hasOwnProperty('ProfitMargin') && item.hasOwnProperty('DebtToEquityRatio');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain DebtorDays, CurrentRatio, Roi, TotalIncome, TotalExpense, Profit, PercentProfit, CashBalance, SafetyBankBalance, AverageMonthlyRevenue, ProfitMargin, and DebtToEquityRatio.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'DebtorDays' ||
      field.column.key === 'CurrentRatio' ||
      field.column.key === 'Roi' ||
      field.column.key === 'TotalIncome' ||
      field.column.key === 'TotalExpense' ||
      field.column.key === 'Profit' ||
      field.column.key === 'PercentProfit' ||
      field.column.key === 'CashBalance' ||
      field.column.key === 'SafetyBankBalance' ||
      field.column.key === 'AverageMonthlyRevenue ' ||
      field.column.key === 'ProfitMargin' ||
      field.column.key === 'DebtToEquityRatio'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id)) {
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      }
      return row;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };

  const financialMetricColumns = useMemo(() => {
    let financial_metric_columns = [
      SelectColumn,
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DebtorDays',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Debtor Days</b>
            {frozenColumns.indexOf('DebtorDays') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DebtorDays')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DebtorDays')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DebtorDays')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DebtorDays')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 145,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.DebtorDays}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DebtorDays: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('DebtorDays') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.DebtorDays}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                DebtorDays: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'CurrentRatio',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Current Ratio</b>
            {frozenColumns.indexOf('CurrentRatio') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'CurrentRatio')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'CurrentRatio')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'CurrentRatio')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'CurrentRatio',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 155,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.CurrentRatio}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    CurrentRatio: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('CurrentRatio') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.CurrentRatio}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                CurrentRatio: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'Roi',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Roi</b>
            {frozenColumns.indexOf('Roi') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Roi')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Roi')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Roi')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Roi')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 125,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Roi}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Roi: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Roi') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Roi}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Roi: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'TotalIncome',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Total Income</b>
            {frozenColumns.indexOf('TotalIncome') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'TotalIncome')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'TotalIncome')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'TotalIncome')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'TotalIncome')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 155,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.TotalIncome}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    TotalIncome: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('TotalIncome') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.TotalIncome}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                TotalIncome: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'TotalExpense',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Total Expense</b>
            {frozenColumns.indexOf('TotalExpense') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'TotalExpense')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'TotalExpense')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'TotalExpense')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'TotalExpense',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 160,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.TotalExpense}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    TotalExpense: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('TotalExpense') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.TotalExpense}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                TotalExpense: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'Profit',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Profit</b>
            {frozenColumns.indexOf('Profit') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Profit')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Profit')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Profit')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Profit')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 95,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Profit}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Profit: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Profit') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Profit}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Profit: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'PercentProfit',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Percent Profit</b>
            {frozenColumns.indexOf('PercentProfit') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'PercentProfit')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'PercentProfit')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'PercentProfit')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'PercentProfit',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 160,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.PercentProfit}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    PercentProfit: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('PercentProfit') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.PercentProfit}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                PercentProfit: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'CashBalance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Cash Balance</b>
            {frozenColumns.indexOf('CashBalance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'CashBalance')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'CashBalance')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'CashBalance')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'CashBalance')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 155,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.CashBalance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    CashBalance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('CashBalance') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.CashBalance}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                CashBalance: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'SafetyBankBalance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Safety Balance</b>
            {frozenColumns.indexOf('SafetyBankBalance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'SafetyBankBalance')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'SafetyBankBalance')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'SafetyBankBalance',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'SafetyBankBalance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 165,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.SafetyBankBalance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    SafetyBankBalance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('SafetyBankBalance') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.SafetyBankBalance}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                SafetyBankBalance: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'AverageMonthlyRevenue',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Avg Monthly Revenue</b>
            {frozenColumns.indexOf('AverageMonthlyRevenue') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'AverageMonthlyRevenue')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'AverageMonthlyRevenue')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'AverageMonthlyRevenue',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'AverageMonthlyRevenue',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 220,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.AverageMonthlyRevenue}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    AverageMonthlyRevenue: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('AverageMonthlyRevenue') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.AverageMonthlyRevenue}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                AverageMonthlyRevenue: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ProfitMargin',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Profit Margin</b>
            {frozenColumns.indexOf('ProfitMargin') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProfitMargin')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProfitMargin')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ProfitMargin')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ProfitMargin',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ProfitMargin}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ProfitMargin: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ProfitMargin') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ProfitMargin}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ProfitMargin: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'DebtToEquityRatio',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Debt to EQ Ratio</b>
            {frozenColumns.indexOf('DebtToEquityRatio') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DebtToEquityRatio')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DebtToEquityRatio')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'DebtToEquityRatio',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DebtToEquityRatio',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 180,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.DebtToEquityRatio}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DebtToEquityRatio: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('DebtToEquityRatio') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.DebtToEquityRatio}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                DebtToEquityRatio: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
    ];

    if (orgLength.length > 1) {
      financial_metric_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      financial_metric_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = financial_metric_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = financial_metric_columns.filter(
      (col) => col.frozen === false,
    );
    financial_metric_columns = [...frozenCols, ...unfrozenCols];

    return financial_metric_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    orgLength,
    dates,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DebtorDays
          ? r.DebtorDays?.toString().includes(filters.DebtorDays.toString())
          : true) &&
        (filters.CurrentRatio
          ? r.CurrentRatio?.toString().includes(filters.CurrentRatio.toString())
          : true) &&
        (filters.Roi
          ? r.Roi?.toString().includes(filters.Roi.toString())
          : true) &&
        (filters.TotalIncome
          ? r.TotalIncome?.toString().includes(filters.TotalIncome.toString())
          : true) &&
        (filters.TotalExpense
          ? r.TotalExpense?.toString().includes(filters.TotalExpense.toString())
          : true) &&
        (filters.Profit
          ? r.Profit?.toString().includes(filters.Profit.toString())
          : true) &&
        (filters.PercentProfit
          ? r.PercentProfit?.toString().includes(
              filters.PercentProfit.toString(),
            )
          : true) &&
        (filters.CashBalance
          ? r.CashBalance?.toString().includes(filters.CashBalance.toString())
          : true) &&
        (filters.SafetyBankBalance
          ? r.SafetyBankBalance?.toString().includes(
              filters.SafetyBankBalance.toString(),
            )
          : true) &&
        (filters.AverageMonthlyRevenue
          ? r.AverageMonthlyRevenue?.toString().includes(
              filters.AverageMonthlyRevenue.toString(),
            )
          : true) &&
        (filters.ProfitMargin
          ? r.ProfitMargin?.toString().includes(filters.ProfitMargin.toString())
          : true) &&
        (filters.DebtToEquityRatio
          ? r.DebtToEquityRatio?.toString().includes(
              filters.DebtToEquityRatio.toString(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportData = useMemo(() => {
    let export_data = [];

    if (exportAllData) {
      export_data = [...tableRows];
    } else {
      if (startDate && endDate) {
        let start_date =
          new Date(startDate).getFullYear() * 10000 +
          (new Date(startDate).getMonth() + 1) * 100 +
          1;
        let end_date =
          new Date(endDate).getFullYear() * 10000 +
          (new Date(endDate).getMonth() + 1) * 100 +
          1;
        export_data = [...tableRows];
        export_data = export_data.filter(
          (data) =>
            data.DimDateKey >= start_date && data.DimDateKey <= end_date,
        );
      }
    }

    return export_data;
  }, [exportAllData, tableRows, startDate, endDate]);

  const exportValue = useMemo(() => {
    let export_value = {
      'Financial Metric': {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Date: {
        columns: [],
        data: [],
      },
    };

    export_value['Financial Metric'].columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'DimDateKey', width: { wpx: 80 } },
      { title: 'DebtorDays', width: { wpx: 80 } },
      { title: 'CurrentRatio', width: { wpx: 80 } },
      { title: 'Roi', width: { wpx: 80 } },
      { title: 'TotalIncome', width: { wpx: 80 } },
      { title: 'TotalExpense', width: { wpx: 80 } },
      { title: 'Profit', width: { wpx: 80 } },
      { title: 'PercentProfit', width: { wpx: 80 } },
      { title: 'CashBalance', width: { wpx: 80 } },
      { title: 'SafetyBankBalance', width: { wpx: 80 } },
      { title: 'AverageMonthlyRevenue', width: { wpx: 80 } },
      { title: 'ProfitMargin', width: { wpx: 80 } },
      { title: 'DebtToEquityRatio', width: { wpx: 80 } },
    ];
    exportData.forEach((data) => {
      export_value['Financial Metric'].data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimDateKey ? Number(data.DimDateKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DebtorDays ? Number(data.DebtorDays) : 0,
          style: { numFmt: '0' },
        },
        {
          value: data.CurrentRatio ? Number(data.CurrentRatio) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.Roi ? Number(data.Roi) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.TotalIncome ? Number(data.TotalIncome) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.TotalExpense ? Number(data.TotalExpense) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.Profit ? Number(data.Profit) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.PercentProfit ? Number(data.PercentProfit) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.CurrentRatio ? Number(data.CurrentRatio) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.CashBalance ? Number(data.CashBalance) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.SafetyBankBalance ? Number(data.SafetyBankBalance) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.AverageMonthlyRevenue
            ? Number(data.AverageMonthlyRevenue)
            : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.ProfitMargin ? Number(data.ProfitMargin) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.DebtToEquityRatio ? Number(data.DebtToEquityRatio) : 0,
          style: { numFmt: '0.00' },
        },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Date.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Date', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value.Date.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]);
      });

    return export_value;
  }, [exportData, companies, organisations, dates]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={financialMetricColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel filename="Financial Metric" value={exportValue} />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

FinancialMetricTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default FinancialMetricTable;
